import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, useFormikContext } from 'formik';
import { get, isEmpty, omit, snakeCase } from 'lodash';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import { TextFieldFormik } from '~/components/TextFieldFormik';
import cn from '~/Utils/cn';

import ReorderDragHandle from '../../components/ReorderDragHandle';
import MoveArrows from '../../MoveArrows/MoveArrows';
import { isNewRow, markNewRow } from '../../newRowIdentity';
import { reorderArrayField } from '../../reorderArrayField';
import SortAzButton from '../../SortAzButton/SortAzButton';

const getReactKeyFromArgs = (...args) => {
  return args.join('_');
};

// Spread a row's fields across the full 12-column grid. The column mix — and therefore the spans —
// depends on how many fields the row has: enabling the Clive additional-instructions field makes a
// sub-type row five fields wide. The five-field case was unhandled, so Display name / Key / Category
// fell back to one column each and the fields bunched into the row's left two-thirds. A field with no
// matching span falls through to the grid's one-column default; in the current configs every field
// hits an explicit span above (the toggle carries its own `col-span-1`), so the default is only a
// safety net for a not-yet-modelled column mix.
const getFieldColumnSpanClassName = ({ columnId, numberOfCompInRow, isFirst }) =>
  cn({
    'pl-0': isFirst,
    'col-span-5': numberOfCompInRow === 3 && (columnId === 'desc' || columnId === 'id'),
    'col-span-4': numberOfCompInRow === 4 && columnId === 'desc',
    'col-span-3':
      columnId === CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY ||
      (numberOfCompInRow === 4 && ['id', 'incident_sub_type_categories', 'category'].includes(columnId)) ||
      (numberOfCompInRow === 5 && columnId === 'desc'),
    'col-span-2': numberOfCompInRow === 5 && ['id', 'incident_sub_type_categories', 'category'].includes(columnId),
    'col-span-1 mt-12': columnId === 'active',
  });

const ArrayFieldFormik = ({
  fieldId,
  innerObjectConfig,
  initialNewItemValues,
  label = '',
  disabled,
  disableAddBtn,
  withSortAz = true,
  withMoveArrows = true,
  withDndReorder = false,
  addBtnLabel = 'ADD OPTION',
  originalFieldId,
}) => {
  const { values, initialValues, touched, setFieldValue } = useFormikContext();

  const initialValue = get(initialValues, originalFieldId ?? fieldId);
  const currentValue = get(values, fieldId);

  // A row added in this editing session is never an existing value, however its key ends up
  // spelled: the key is re-derived from the display name on every keystroke while the row is new,
  // so a key-only check calls the row existing the moment it spells a saved row's key - taking away
  // its Key and its delete button with no way back. A row that came from the server carries no
  // marker and keeps the key match, so its verdict is unchanged. One predicate reaches all three
  // consumers: the delete-vs-hide affordance, the disableEditAfterInitialSet lock, and the key sync.
  // Both render paths below gate their row loop on `Array.isArray(currentValue)`, so this only ever
  // runs for a row that exists - indexing `currentValue` needs no guard.
  const getInInitialValues = (index) => {
    if (isNewRow(currentValue[index])) {
      return false;
    }
    const currentValueId = currentValue[index]?.id;
    const initialIds = initialValue?.map((item) => item.id);
    return !isEmpty(currentValueId) && !isEmpty(initialIds) && initialIds?.includes(currentValueId);
  };

  // When the config pairs a `desc` (display) with an `id` (key), fill the key from the display
  // name while the user types — edit-driven, so a reorder (rows are keyed by position) can't be
  // read as an edit and rewrite the key.
  const syncsKeyFromDesc =
    innerObjectConfig?.some((f) => f.id === 'id') && innerObjectConfig?.some((f) => f.id === 'desc');

  const getFieldByType = (fieldConfig, index, arrayHelpers) => {
    const { id, type, label, disableEditAfterInitialSet, customComponent, additionalProps } = fieldConfig;
    const path = `${fieldId}[${index}].${id}`;
    const args = {
      id: path,
      label,
      onDelete: () => arrayHelpers.remove(index),
      isExistingValue: getInInitialValues(index),
      columnId: id,
      ...additionalProps,
    };

    const commonProps = {
      disableEditAfterInitialSet,
    };

    switch (type) {
      case 'text': {
        const textArgs = { ...omit(args, ['onDelete', 'isExistingValue']), fullWidth: true };
        if (syncsKeyFromDesc && id === 'desc') {
          const keyPath = `${fieldId}[${index}].id`;
          const isExistingRow = getInInitialValues(index);
          textArgs.onChange = (e) => {
            const newDesc = e.target.value;
            setFieldValue(path, newDesc);
            // Only fill the key for a new row whose key the user hasn't edited (matches the
            // previous SwitchOrDelete guard: disabled when isExistingValue || key touched).
            if (!isExistingRow && !get(touched, keyPath)) {
              setFieldValue(keyPath, snakeCase(newDesc));
            }
          };
        }
        return {
          Comp: TextFieldFormik,
          args: textArgs,
          ...commonProps,
        };
      }
      case 'custom':
        return {
          Comp: customComponent,
          args: { ...args },
          ...commonProps,
        };
    }
  };

  return (
    <FieldArray
      name={fieldId}
      render={(arrayHelpers) => (
        <div className="mr-6 flex flex-col gap-20">
          <div className="flex justify-between">
            <Text>{label}</Text>
            {withSortAz && <SortAzButton fieldId={fieldId} sortKey="desc" disabled={disabled} />}
          </div>
          {withDndReorder ? (
            <DndOrderedList
              className="flex flex-col gap-20"
              disabled={disabled}
              items={(Array.isArray(currentValue) ? currentValue : []).map((row, i) => ({
                row,
                index: i,
                // Key on the position, not the row's `id`. Field ids are user-editable (Display Name
                // syncs to Key via snakeCase), so an id-derived key changes mid-edit and remounts the
                // row, dropping input focus. The index is stable while typing and unique for a drag
                // (the list can't reorder mid-gesture).
                dndId: String(i),
              }))}
              getItemId={(item) => item.dndId}
              onReorder={({ fromIndex, toIndex }) => reorderArrayField(arrayHelpers, fromIndex, toIndex)}
              renderItem={({ index: i, dndId }, { dragHandleProps }) => (
                <div className="grid grid-cols-12 gap-20">
                  {innerObjectConfig.map((field, j) => {
                    const { Comp, args, disableEditAfterInitialSet } = getFieldByType(field, i, arrayHelpers);
                    const numberOfCompInRow = innerObjectConfig?.length;

                    return (
                      <div
                        key={getReactKeyFromArgs(fieldId, field.id, dndId, j)}
                        className={getFieldColumnSpanClassName({
                          columnId: args?.columnId,
                          numberOfCompInRow,
                          isFirst: j === 0,
                        })}
                      >
                        <Comp
                          {...omit(args, ['columnId'])}
                          disabled={disabled || (disableEditAfterInitialSet && getInInitialValues(i))}
                        />
                      </div>
                    );
                  })}
                  <div className="col-span-1 mt-12 box-border flex items-center justify-end">
                    <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={disabled} iconSize={24} />
                  </div>
                </div>
              )}
            />
          ) : (
            Array.isArray(currentValue) &&
            currentValue.map((value, i) => (
              <div className="grid grid-cols-12 gap-20" key={getReactKeyFromArgs(fieldId, i)}>
                {innerObjectConfig.map((field, j) => {
                  const { Comp, args, disableEditAfterInitialSet } = getFieldByType(field, i, arrayHelpers);
                  const numberOfCompInRow = innerObjectConfig?.length;

                  return (
                    <div
                      key={getReactKeyFromArgs(fieldId, field.id, i, j)}
                      className={getFieldColumnSpanClassName({
                        columnId: args?.columnId,
                        numberOfCompInRow,
                        isFirst: j === 0,
                      })}
                    >
                      <Comp
                        {...omit(args, ['columnId'])}
                        disabled={disabled || (disableEditAfterInitialSet && getInInitialValues(i))}
                      />
                    </div>
                  );
                })}
                {withMoveArrows && (
                  <div className="col-span-1 mt-12 box-border flex items-center justify-end">
                    <MoveArrows
                      onMoveUp={() => arrayHelpers.move(i, Math.max(i - 1, 0))}
                      onMoveDown={() => arrayHelpers.move(i, Math.min(i + 1, currentValue.length - 1))}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          <ErrorHelperTextFormik id={fieldId} withoutChildren />
          <div className="flex">
            <Button
              variant="text"
              color="primary"
              onClick={() => arrayHelpers.push(markNewRow(initialNewItemValues))}
              disabled={disableAddBtn || disabled}
            >
              <AddIcon />
              {addBtnLabel}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

ArrayFieldFormik.propTypes = {
  fieldId: PropTypes.string.isRequired,
  innerObjectConfig: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'switch', 'checkbox', 'date', 'custom', 'multiselect']).isRequired,
      additionalProps: PropTypes.object,
      disableEditAfterInitialSet: PropTypes.bool,
      customComponent: PropTypes.elementType,
    })
  ).isRequired,
  initialNewItemValues: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  disableAddBtn: PropTypes.bool,
  withSortAz: PropTypes.bool,
  withMoveArrows: PropTypes.bool,
  withDndReorder: PropTypes.bool,
  addBtnLabel: PropTypes.string,
  originalFieldId: PropTypes.string,
};

export default ArrayFieldFormik;
